import type { Global } from '../../types';

import type { PointerLockManager } from './pointer-lock';

const isCaptureMode = (mode: string) => mode === 'walk' || mode === 'fly';

const isWasdKey = (event: KeyboardEvent) =>
    event.code === 'KeyW' || event.code === 'KeyA' || event.code === 'KeyS' || event.code === 'KeyD';

// The shortcut a key press stands for. A key that types a Latin character keeps using it, so
// the shortcuts follow the key labels (Dvorak moves them, as it should). A key that types
// anything else, such as Cyrillic or Greek, falls back to its position on a US layout, the way
// WASD already works, so the shortcuts still work on every layout the ui is translated for.
const shortcutKey = (event: KeyboardEvent) => {
    if (/^[\x20-\x7e]$/.test(event.key)) {
        return event.key;
    }
    const match = /^(?:Key([A-Z])|Digit(\d))$/.exec(event.code);
    return match ? (match[1]?.toLowerCase() ?? match[2]) : event.key;
};

/**
 * Keyboard shortcuts that switch camera mode and toggle UI affordances.
 * Listens on `window` so the user can press 1/2/3, V, G, H, F, R, Space,
 * or Escape regardless of which element has focus — unless the host has
 * cleared `state.inputEnabled`, which is how it routes the keyboard away.
 */
class ModeShortcuts {
    private _global: Global | null = null;

    private _pointerLock: PointerLockManager | null = null;

    private _onKeyDown = (event: KeyboardEvent) => {
        const global = this._global;
        if (!global) return;
        const { state, events } = global;
        if (!state.inputEnabled) return;

        if (event.key === 'Escape') {
            if (this._pointerLock?.recentlyExitedCapture) {
                // already handled by pointerlockchange
            } else if (isCaptureMode(state.cameraMode) && state.gamingControls && state.inputMode === 'desktop') {
                state.gamingControls = false;
            } else if (state.cameraMode === 'walk') {
                events.fire('inputEvent', 'exitWalk', event);
            } else {
                events.fire('inputEvent', 'cancel', event);
            }
            return;
        }

        if (event.ctrlKey || event.altKey || event.metaKey) {
            return;
        }

        const key = shortcutKey(event);

        switch (key) {
            case '1':
                state.cameraMode = 'orbit';
                break;
            case '2':
                state.cameraMode = 'fly';
                break;
            case '3':
                events.fire('inputEvent', 'toggleWalk');
                break;
            case 'v':
                if (state.hasCollisionOverlay) {
                    state.collisionOverlayEnabled = !state.collisionOverlayEnabled;
                }
                break;
            case 'g':
                state.gamingControls = !state.gamingControls;
                break;
            // H the controls panel, Shift+H the info panel. Shift is read from the event, so
            // caps lock does not swap them
            case 'h':
            case 'H':
                events.fire('inputEvent', event.shiftKey ? 'toggleHelp' : 'toggleControls');
                break;
            case 'r':
                events.fire('inputEvent', 'reset', event);
                break;
            default:
                if (isWasdKey(event) && state.inputMode === 'desktop') {
                    if (!isCaptureMode(state.cameraMode)) {
                        events.fire('inputEvent', 'requestFirstPerson');
                    }
                    if (!state.gamingControls) {
                        state.gamingControls = true;
                    }
                }
                break;
        }

        if (state.cameraMode !== 'walk') {
            switch (key) {
                case 'f':
                    events.fire('inputEvent', 'frame', event);
                    break;
                case ' ':
                    events.fire('inputEvent', 'playPause', event);
                    break;
            }
        }
    };

    attach(global: Global, pointerLock: PointerLockManager): void {
        this._global = global;
        this._pointerLock = pointerLock;
        window.addEventListener('keydown', this._onKeyDown);
    }

    detach(): void {
        window.removeEventListener('keydown', this._onKeyDown);
        this._global = null;
        this._pointerLock = null;
    }
}

export { ModeShortcuts };
